import { useMemo } from "react"
import {
  Box,
  BoxProps,
  Image,
  Text,
  ThemingProps,
  useMultiStyleConfig,
} from "@chakra-ui/react"

import matsIconBlack from "#/assets/mats-icon-black.svg"
import matsIconWhite from "#/assets/mats-icon-white.svg"
import TooltipLabel from "#/shared/components/TooltipLabel"

export type MatsDisplayWidgetProps = BoxProps &
  Omit<ThemingProps, "orientation" | "colorScheme" | "styleConfig"> & {
    mats?: string | number
    invertColors?: boolean
    tooltipOnly?: boolean
    tooltipWithLightShadow?: boolean
    tooltipPlacement?: "top" | "bottom" | "left" | "right" | undefined
  }

export default function MatsDisplayWidget({
  mats,
  invertColors = false,
  variant = "badge",
  size = "sm",
  tooltipOnly = false,
  tooltipWithLightShadow = false,
  tooltipPlacement = "bottom",
  children,
  ...props
}: MatsDisplayWidgetProps) {
  const styles = useMultiStyleConfig("MatsDisplayWidget", { variant, size })

  /**
   * Workaround for mats displayed in account panel next to deposit form
   * Thanks to this the text won't go over the account panel border
   */
  const matsStyles = useMemo(() => {
    if (size === "xl" && variant === "accountPanelLabel") {
      if (String(mats).length > 7) {
        styles.mats.fontSize = "50px"
      } else if (String(mats).length > 6) {
        styles.mats.fontSize = "56px"
      }
    }
    return styles.mats
  }, [size, variant, styles.mats, mats])

  return (
    <TooltipLabel
      value={
        <Box display="inline-flex" alignItems="center" gap="4px">
          <Text as="span">Points are now mats</Text>
          <Image src={matsIconWhite} width="16px" />
        </Box>
      }
      props={{
        boxShadow: tooltipWithLightShadow
          ? "0 0 10px rgba(255, 255, 255, 0.4)"
          : "none",
      }}
      placement={tooltipPlacement}
    >
      {!tooltipOnly ? (
        <Box __css={styles.mainContainer} {...props}>
          {(mats || mats === 0) && <Box __css={matsStyles}>{mats}</Box>}
          <Box __css={styles.iconContainer}>
            <Image
              src={invertColors ? matsIconWhite : matsIconBlack}
              __css={styles.icon}
            />
            <Text
              as="span"
              color={invertColors ? "primary.white.100" : "primary.black.100"}
            >
              /day
            </Text>
          </Box>
        </Box>
      ) : (
        children
      )}
    </TooltipLabel>
  )
}
